import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { GradientModal } from '../ui';
import CatalogBrowser from './CatalogBrowser';
import QuickAddSeedModal from './QuickAddSeedModal';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { supabase } from '../../supabaseClient';
import { buildSeedInsertFromCatalog } from '../../utils';
import { CATALOG_CHIPS } from '../../utils/constants';

/**
 * Standalone "Browse Catalog" modal powered by CatalogModalContext.
 *
 * Props:
 *   isOpen    {boolean}
 *   onSelect  {(seedRecord) => void} — called with the inserted seeds row
 *   onClose   {() => void}
 *
 * When the user taps a plant in the catalog:
 *   - If already in their seeds inventory → onSelect is called immediately
 *     with the existing seed row.
 *   - If new → the seed row is inserted first, then onSelect is called.
 *
 * When no onSelect callback is provided (pure browse mode) the modal just
 * closes after adding.
 */
const CatalogModal = ({ isOpen, onSelect, onClose, onToast }) => {
    const { user } = useAuth();
    const { isPro, getLimits } = useSubscription();
    const limits = getLimits();

    const [seeds, setSeeds] = useState([]);
    const [showQuickAdd, setShowQuickAdd] = useState(false);

    // Filter state lifted here so it can live in stickyContent (outside scroll area)
    const [activeChip, setActiveChip] = useState('vegetables');
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    // Reset filters when modal closes
    useEffect(() => {
        if (!isOpen) {
            setActiveChip('vegetables');
            setSearchTerm('');
        } else {
            // Auto-focus search when opened; clean up if modal closes before timer fires
            const timerId = setTimeout(() => searchInputRef.current?.focus(), 50);
            return () => clearTimeout(timerId);
        }
    }, [isOpen]);

    // Load seeds so CatalogBrowser can mark already-added items
    useEffect(() => {
        if (!isOpen || !user) return;
        supabase
            .from('seeds')
            .select('*')
            .eq('user_id', user.id)
            .then(({ data }) => {
                if (data) setSeeds(data);
            });
    }, [isOpen, user]);

    const handleAdd = useCallback(async (catalogRecord) => {
        if (!user) return;

        // If already in inventory, find & return the existing seed row
        // Check catalog_id first, then fall back to plant+variety match for manually-added seeds
        const existing = seeds.find(s =>
            (catalogRecord.id && s.catalog_id === catalogRecord.id) ||
            (s.plant === catalogRecord.plant && (s.variety || '') === (catalogRecord.variety || ''))
        );
        if (existing) {
            onSelect?.(existing);
            return;
        }

        // Free-tier limit check
        if (!isPro && seeds.length >= limits.seeds) {
            alert(`You've reached the free limit of ${limits.seeds} plants. Upgrade to Pro to add more.`);
            return;
        }

        const seedData = buildSeedInsertFromCatalog(user.id, catalogRecord);

        const { data, error } = await supabase
            .from('seeds')
            .insert(seedData)
            .select()
            .single();

        if (error) {
            alert("Couldn't add plant — please try again.");
            return;
        }

        // Optimistically update local seeds list so the plant shows "Added"
        setSeeds(prev => [...prev, data]);

        // Broadcast so SeedInventory (if visible) refreshes
        window.dispatchEvent(
            new CustomEvent('seed-added-externally', { detail: { seed: data } })
        );

        // Show confirmation toast (rendered at provider level so it outlives modal close)
        onToast?.({
            message: `${data.plant}${data.variety ? ` (${data.variety})` : ''} added to My Plants`,
            seed: data,
        });

        onSelect?.(data);
    }, [user, isPro, seeds, limits.seeds, onSelect, onToast]);

    const chipLabel = CATALOG_CHIPS.find(c => c.id === activeChip)?.label || '';

    const filterBar = (
        <div>
            {/* Category chips */}
            <div className="flex flex-wrap gap-2 mb-3">
                {CATALOG_CHIPS.filter(chip => chip.id !== 'all').map(chip => (
                    <button
                        key={chip.id}
                        onClick={() => setActiveChip(chip.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${activeChip === chip.id
                            ? 'bg-[#52A675] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        {chip.label}
                    </button>
                ))}
            </div>
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={`Search in ${chipLabel}\u2026`}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-8 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white text-sm"
                />
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Clear search"
                    >
                        <X size={15} />
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <>
            <GradientModal
                isOpen={isOpen}
                onClose={onClose}
                title="Browse Catalog"
                subtitle="Tap a plant to add it to My Plants"
                maxWidth="max-w-2xl"
                zIndex="z-[70]"
                primaryAction={{ label: 'Done', onClick: onClose }}
                stickyContent={filterBar}
                footerExtra={
                    <button
                        onClick={() => setShowQuickAdd(true)}
                        className="text-sm text-[#52A675] hover:text-[#4A9168] font-medium transition-colors"
                    >
                        Can&apos;t find your plant? Add it custom &rarr;
                    </button>
                }
            >
                <CatalogBrowser
                    seeds={seeds}
                    isPro={isPro}
                    seedLimit={limits.seeds}
                    onAdd={handleAdd}
                    onSwitchToAdd={() => {
                        onClose();
                        window.dispatchEvent(new CustomEvent('app-navigate', { detail: { tab: 'seeds' } }));
                        window.dispatchEvent(new CustomEvent('seeds-open-add-form'));
                    }}
                    simplified={true}
                    activeChip={activeChip}
                    onChipChange={setActiveChip}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                />
            </GradientModal>

            <QuickAddSeedModal
                isOpen={showQuickAdd}
                onClose={() => setShowQuickAdd(false)}
                onSave={(newSeed) => {
                    // Add to local seed list so it shows as "Added" in the browser
                    setSeeds(prev => [...prev, newSeed]);
                    // Forward to any listener (e.g. GardenLayoutPlanner adds to plan)
                    onSelect?.(newSeed);
                    onToast?.({
                        message: `${newSeed.plant}${newSeed.variety ? ` (${newSeed.variety})` : ''} added to My Plants`,
                        seed: newSeed,
                    });
                    setShowQuickAdd(false);
                }}
                isPro={isPro}
                seedLimit={limits.seeds}
                currentSeedCount={seeds.length}
            />

        </>
    );
};

export default CatalogModal;
